import { ref } from 'vue'
import { githubApi } from '@/api/github'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import type { PaginationMeta, RepoScan, RepoScanFilters } from '@/types/models'

// Singleton, following useReviewRuns.ts's shape: shared state so the repo
// scans view retains its list/pagination across navigation within a session.
const repoScans = ref<RepoScan[]>([])
const meta = ref<PaginationMeta | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

// Guards against a stale filter/page request's response landing after a
// newer one's and clobbering it.
const requestGuard = createRequestGuard()

export function useRepoScans() {
  async function fetchRepoScans(installationId: string, filters: RepoScanFilters = {}): Promise<void> {
    if (!installationId) return
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load repo scan history.',
      async () => {
        const page = await githubApi.listInstallationRepoScans(installationId, filters)
        if (!requestGuard.isCurrent(token)) return
        repoScans.value = page.data
        meta.value = page.meta
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  // Called on logout so the next session's repo scans view doesn't briefly
  // show the previous user's data before its own fetch resolves, and so a
  // late response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    requestGuard.start()
    repoScans.value = []
    meta.value = null
    loading.value = false
    error.value = null
  }

  return {
    repoScans,
    meta,
    loading,
    error,
    fetchRepoScans,
    reset,
  }
}
